import React from 'react';
import { useNavigate } from 'react-router-dom';

import Empty from 'antd/es/empty';

import { useApplicationContext } from '@common/react/components/Core/Application/Application';
import { getDefaultOptions } from '@common/react/components/UI/Transmutation/defaultOptions';
import { WithId } from '@common/typescript/objects/WithId';
import {
	SearchObject,
	SearchObjectType,
	CustomItemDelegate,
} from '@common/react/components/UI/SearchModal/index';
import { isTextInclude } from '@common/react/components/Utils/Utils';
import SearchModalItem from '@common/react/components/UI/SearchModal/SearchModalItem';
import { MenuItem } from '@common/react/objects/MenuItem';
import { getLocalStorageValue, setLocalStorageValue } from '@common/react/utils/localStorage/localStorage';

interface SearchMenuProps<T extends number> {
	search: string;
	items: Array<MenuItem>;
	loading: boolean;
	searchItems: Array<SearchObject<T>>;
	open: boolean;
	allowTransmutation: boolean;
	getPathFromType: (type: T) => string;
	additional?: (item: SearchObject<T>) => React.ReactNode;
	customItem?: CustomItemDelegate<T>;
	lastItems?: Array<SearchObject<T>>;
}

const updateLastItems = <T extends number>(item: SearchObject<T>, userId?: number) => {
	const key = `${userId ?? ''}-search-modal-last-items`;
	const lastItems = JSON.parse(getLocalStorageValue(key, '[]'));
	const newLastItems = [
		item,
		...lastItems.filter((lastItem) => (item.menuItem ? item.menuItem?.path !== lastItem.menuItem?.path
			: `${lastItem.type}${lastItem.id}` !== `${item.type}${item.id}`)),
	].slice(0, 7);
	setLocalStorageValue(key, JSON.stringify(newLastItems));
	return newLastItems;
};

const SearchMenu: <T extends number>(
	p: SearchMenuProps<T>
) => React.ReactElement<T> = <T extends number>(
	props: SearchMenuProps<T>,
) => {
	const {
		onTransmutation, request, getUser,
	} = useApplicationContext();
	const {
		items,
		search,
		searchItems,
		loading,
		open,
		allowTransmutation,
		getPathFromType,
		customItem,
		additional,
		lastItems,
	} = props;
	const navigate = useNavigate();
	const user = getUser();

	const [userLoadingId, setUserLoadingId] = React.useState(0);

	const menuItems = React.useMemo(() => {
		const res: Array<SearchObject<T>> = searchItems;
		const leftMenu = search
			? (items || [])
				.reduce((acc: Array<MenuItem>, value: MenuItem) => {
					let current = value;
					const includeText = isTextInclude(search, value.name);
					if (value.name === 'External Services') {
						return acc;
					}

					if (value.children && !includeText) {
						current = JSON.parse(JSON.stringify(value));
						current.children!.list = current.children!.list.filter((el) => isTextInclude(search, el.name));
					}

					return (includeText || current.children?.list.length ? [...acc, value] : [...acc]) as Array<MenuItem>;
				}, [])
				.reduce((acc: Array<MenuItem>, value: MenuItem) => {
					if (value.children) {
						const isInclude = isTextInclude(search, value.name);
						return [
							...acc,
							{
								...value,
								group: true,
								children: undefined,
							},
							...value.children!.list
								.filter((item) => isInclude || isTextInclude(search, item.name))];
					}

					return [...acc, value];
				}, [])
				.map((item, index) => ({
					id: index,
					type: SearchObjectType.Menu as T,
					firstName: item.name,
					group: 'group' in item ? item.group : undefined,
					lastName: '',
					avatar: '',
					menuItem: item,
				}))
			: [];
		return search ? res.concat(leftMenu) : lastItems ?? [];
	}, [items, search, searchItems, loading]);

	const [index, setIndex] = React.useState<number>(() => (menuItems[0]?.group ? 1 : 0));

	const onInputKeyDown = (e, items: Array<SearchObject<T>> = menuItems) => {
		if (e.code === 'ArrowDown') {
			e.preventDefault();
			setIndex((prev) => {
				const nextIndex = items?.[prev + 1]?.group ? prev + 2 : prev + 1;
				if (nextIndex >= items.length) {
					const index = items?.[0]?.group ? 1 : 0;
					if (items[index]) {
						items[index].scrollIntoView = true;
					}
					return index;
				}
				if (items[index]) {
					items[nextIndex].scrollIntoView = true;
				}
				return nextIndex;
			});
		}

		if (e.code === 'ArrowUp') {
			e.preventDefault();
			setIndex((prev) => {
				const nextIndex = items?.[prev - 1]?.group ? prev - 2 : prev - 1;
				if (nextIndex <= -1) {
					if (items[items.length - 1]) {
						items[items.length - 1].scrollIntoView = true;
					}
					return items.length - 1;
				}
				if (items[nextIndex]) {
					items[nextIndex].scrollIntoView = true;
				}
				return nextIndex;
			});
		}

		if (e.code === 'Enter') {
			setIndex((prev) => {
				const item = items[prev];
				if (item) {
					const state = { prevPath: `${location.pathname}${location.search}${location.hash}` };
					updateLastItems(item, user?.id);
					if (item.id < 0) return prev;
					if (item.menuItem) {
						if (item.menuItem?.externalLink) {
							const a = document.createElement('a');
							a.target = '_blank';
							a.href = item.menuItem.path;
							a.click();
						}
						navigate(
							`${item.menuItem.basePath || ''}${item.menuItem.path}`,
							{ state },
						);
					} else if (item.id) {
						navigate(`/${getPathFromType(item.type)}-editor/${item.id}`, { state });
					}
				}

				return prev;
			});
		}

		if (e.code === 'KeyB' && (e.ctrlKey || e.metaKey) && open) {
			e.preventDefault();
			setIndex((prev) => {
				if (items[prev] && items[prev].type === SearchObjectType.User) {
					const id = items[prev].id;

					const {
						requestType,
						requestProps,
						onSuccess,
						onError,
					} = getDefaultOptions<WithId>(id > 0 ? id : 0, onTransmutation);

					setUserLoadingId(id);

					request<WithId>(requestType, requestProps)
						.then(() => {
							onSuccess();
							updateLastItems(items[prev], user?.id);
						})
						.catch(onError)
						.finally(() => {
							setUserLoadingId(-1);
						});
				}

				return prev;
			});
		}
	};

	React.useEffect(() => {
		setIndex(menuItems[0]?.group ? 1 : 0);
	}, [searchItems, search]);

	React.useEffect(() => {
		if (open) {
			document.addEventListener('keydown', onInputKeyDown);
		}
		return () => {
			open && document.removeEventListener('keydown', onInputKeyDown);
		};
	}, [menuItems, open, user?.id]);
	const currentItem = menuItems?.[index];

	const onSelect = (item: SearchObject<T>) => {
		const index = item.id
			? menuItems.findIndex((el) => el.id === item.id && el.type === item.type)
			: menuItems.findIndex((el) => el.firstName === item.firstName && el.path === item.path);
		setIndex(index);
	};

	const onClick = (e, item: SearchObject<T>) => {
		updateLastItems(item, user?.id);
	};

	return <>
		<ul className="search-modal-list">
			{menuItems.map((item) => <SearchModalItem<T>
				getPathFromType={getPathFromType}
				customItem={customItem}
				search={search}
				userLoadingId={userLoadingId}
				allowTransmutation={allowTransmutation}
				isSelected={currentItem?.id === undefined
					? item.firstName === currentItem?.firstName && currentItem?.path === item.path
					: currentItem?.id === item.id && currentItem?.type === item.type}
				onSelect={onSelect}
				item={item}
				onClick={onClick}
				additional={additional}
				currentUserId={user?.id}
				onTransmutation={() => updateLastItems(item, user?.id)}
				key={item.id === undefined ? `${item.path}${item.firstName}` : `${item.id} ${item.type}`}
			/>)}
		</ul>
		{!menuItems.length && !loading
			? (
				<Empty
					image={Empty.PRESENTED_IMAGE_SIMPLE}
					description={search ? `No results for "${search}"` : 'No recent searches'}
				/>
			)
			: null}
	</>;
};

export default SearchMenu;
